/**
 * telemetry.ts — Plan 14-15 (D-v11-15).
 *
 * The single emit point for browser telemetry. Before this, two call sites in
 * VisionGallery hand-built `api.post('/api/internal/event/track', {...})`, and
 * this plan adds three more in the reveal. Five independently-written calls to
 * one endpoint drift, and the way they drift is invisible: a payload key
 * misspelled here does not fail anything, it just produces a column of nulls
 * that nobody notices until the report is being read to settle an argument.
 *
 * ── The type is the contract ───────────────────────────────────────────────
 *
 * `TelemetryEvent` is a discriminated union mirroring `TrackEventRequest`'s
 * allowlist and its payload rules. A call with the wrong payload for an event
 * fails the build rather than the validator, which matters because the
 * validator's answer arrives as a 422 that this module deliberately swallows.
 * Without the type, an emit could be wrong in production and silent forever.
 *
 * When `TrackEventRequest::BROWSER_EVENT_TYPES` changes, this union changes
 * with it. They are two halves of one vocabulary.
 *
 * ── Why it never throws ────────────────────────────────────────────────────
 *
 * Telemetry is not something the user is waiting on. These fire from click
 * handlers and effect cleanups; a rejected promise inside a click handler
 * breaks the click, and an unhandled rejection in a cleanup breaks an unmount.
 * Measurement must not be able to break the thing it measures (T-14-89), which
 * is the same rule the server side follows in ReferencePhotoService::record.
 *
 * This is not the silent fallback CLAUDE.md forbids. Nothing is being made to
 * look like it succeeded: no user-visible outcome depends on the result, and
 * there is no substitute behaviour standing in for a failure. The event is
 * either recorded or it is not.
 */
import { api } from './api-client';

const TRACK_URL = '/api/internal/event/track';

/**
 * The browser vocabulary, mirroring TrackEventRequest::BROWSER_EVENT_TYPES.
 *
 * `vision.viewed` and `anchor.visualized` also count toward Neural Priming.
 * The three `anchor.*` funnel events deliberately do not: they are
 * measurement, and one of them fires on arrival at a screen the user did not
 * ask for. See the docblock on TrackEventRequest for the full reasoning.
 */
export type TelemetryEvent =
  | { event: 'vision.viewed'; payload: { vision_id: string } }
  | { event: 'anchor.visualized'; payload: { vision_id: string } }
  | { event: 'anchor.reveal_shown'; payload: { candidate_set_id: string } }
  | {
      event: 'anchor.candidate_picked';
      payload: {
        candidate_set_id: string;
        /** One-based position among the candidates that ARRIVED, not among those expected. */
        ordinal: number;
        /**
         * How many tiles she was actually choosing between.
         *
         * After Plan 14-14 a partial set is a normal outcome: a candidate can
         * fail terminally and its slot resolves rather than hanging. So
         * "ordinal 2" alone is not a fact about anything. Ordinal 2 of 2 is the
         * last one she looked at; ordinal 2 of 3 is the middle. The ordinal
         * distribution D-v11-15 asks for is uninterpretable without this, and
         * the report splits on it for exactly that reason.
         */
        arrived_count: number;
        time_to_pick_ms: number;
      };
    }
  | {
      event: 'anchor.reveal_abandoned';
      payload: { candidate_set_id: string; dwell_ms: number };
    };

/**
 * Record one event. Resolves regardless of the outcome; never throws.
 *
 * Resolves to whether the event was actually recorded. Almost every call site
 * should ignore that and fire-and-forget. The one that must not is
 * VisionGallery's `vision.viewed`, which writes a sessionStorage key to avoid
 * re-firing on refresh (T-13-24): setting that key after a FAILED emit would
 * suppress the retry and lose the event for the whole session. Returning a
 * boolean rather than throwing keeps that distinction available without
 * putting a rejected promise inside a click handler.
 */
export async function trackEvent(event: TelemetryEvent): Promise<boolean> {
  try {
    await api.post(TRACK_URL, {
      event_type: event.event,
      payload: event.payload,
    });

    return true;
  } catch {
    // Deliberate. See the file docblock: a failed emit must not surface to the
    // user or break the handler it fired from.
    return false;
  }
}
